import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Table } from 'reactstrap';

import {
  Column,
  Table as ReactTable,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

// import { useSelector } from 'react-redux';
// import { RootState } from 'slices';
import {
  EntriesPagination,
  ResultsPagination,
} from 'Components/pos2u/Pagination';
import { useTranslation } from 'react-i18next';
import useWindowSize from 'Components/Hooks/WindowSizeHook';
import Loader from 'Components/Common/Loader';

// Column Filter
const Filter = ({
  column,
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        // value={(columnFilterValue ?? '') as string}
        value={typeof columnFilterValue === 'string' ? columnFilterValue : ''}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control search"
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

interface TableContainerProps {
  columns: ColumnDef<any, any>[];
  data?: any;
  customPageSize: number;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;
  TotalData: number;
  totalPages: number;
  currentPage: number;
  setEntriesPerPage: (value: number) => void;
  handlePageChange: (value: number) => void;
  initialFilters?: any;
  inputPageValue: number | null;
  setInputPageValue: (value: number | null) => void;
  isTableLoadSuccess: boolean;
  // isAdvanceSearch?: boolean;
  // setIsAdvanceSearch?: (value: boolean) => void;
}

const TableWrapper = ({
  columns,
  data,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  TotalData,
  totalPages,
  currentPage,
  setEntriesPerPage,
  handlePageChange,
  inputPageValue,
  setInputPageValue,
  isTableLoadSuccess,
}: // isAdvanceSearch,
// setIsAdvanceSearch,
TableContainerProps) => {
  // const layoutState = useSelector((state: RootState) => state.Layout);
  // const { layoutModeType } = layoutState;
  // const isDarkMode = layoutModeType === 'dark';
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns: columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    // getCanPreviousPage,
    // getCanNextPage,
    // getPageOptions,
    // setPageIndex,
    // nextPage,
    // previousPage,
    setPageSize,
    getState,
  } = table;

  useEffect(() => {
    if (Number(customPageSize)) {
      setPageSize(Number(customPageSize));
    }
  }, [customPageSize, setPageSize]);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 0) {
      handlePageChange(currentPage - 1);
    }
    setInputPageValue(currentPage);
  }, [currentPage, handlePageChange, setInputPageValue]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      handlePageChange(currentPage + 1);
      setInputPageValue(currentPage + 2);
    }
  }, [totalPages, currentPage, handlePageChange, setInputPageValue]);

  return (
    <Fragment>
      {!isMobile && (
        <EntriesPagination
          customPageSize={customPageSize}
          setEntriesPerPage={setEntriesPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          inputPageValue={inputPageValue}
          setInputPageValue={setInputPageValue}
        />
      )}

      <div className={divClass}>
        <Table
          hover
          className={tableClass}
          style={{
            // tableLayout: 'fixed',
            width: '100%',
          }}
        >
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th
                    key={header.id}
                    className={thClass}
                    style={{
                      // overflow: 'hidden',
                      // textOverflow: 'ellipsis',
                      // whiteSpace: 'nowrap',
                      ...header.column.columnDef.meta?.style,
                    }}
                    {...{
                      onClick: header.column.getToggleSortingHandler(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() === 'asc'
                          ? ' '
                          : header.column.getIsSorted() === 'desc'
                          ? ' '
                          : null}
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {isTableLoadSuccess ? (
            <tbody>
              {getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={getHeaderGroups()[0].headers.length}>
                    <div className="text-muted text-center">
                      <span>{t('No results found')}</span>
                    </div>
                  </td>
                </tr>
              ) : (
                getRowModel().rows.map((row: any) => {
                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell: any) => {
                        return (
                          <td
                            key={cell.id}
                            style={{
                              // overflow: 'hidden',
                              // textOverflow: 'ellipsis',
                              // whiteSpace: 'nowrap',
                              ...cell.column.columnDef.meta?.cellStyle,
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
              {getRowModel().rows.length === 1 && (
                <tr>
                  <td colSpan={getHeaderGroups()[0].headers.length}>
                    <div
                      className="text-muted text-center"
                      style={{ minHeight: '80px' }}
                    ></div>
                  </td>
                </tr>
              )}
            </tbody>
          ) : (
            <tr>
              <td colSpan={getHeaderGroups()[0].headers.length}>
                <div className="d-flex justify-content-center align-items-center">
                  <Loader />
                </div>
              </td>
            </tr>
          )}
        </Table>
      </div>

      <ResultsPagination
        showingData={getState().pagination.pageSize}
        TotalData={TotalData}
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        inputPageValue={inputPageValue}
        setInputPageValue={setInputPageValue}
      />
    </Fragment>
  );
};

export default React.memo(TableWrapper);
